import React, {useCallback, useEffect, useMemo, useState} from "react";
import useIsMounted from "../../../../hooks/use-is-mounted";
import AutocompleteBase from "../autocomplete-base";
import {AutocompleteProps, AutocompleteRenderOptionState, TextFieldProps} from "@mui/material";
import {CancelToken} from "axios";
import Api from "../../../../../core/services/api/api";

/**
 * Warehouse option from ReadAllWarehouse.
 * `name` is aliased from `title` so existing label helpers keep working.
 */
export interface WarehouseV2Option {
    code: string;
    title?: string;
    name?: string;
    id?: number;
}

interface WarehouseV2AutocompleteProps<M extends boolean = false> extends Omit<AutocompleteProps<WarehouseV2Option, M, false, false>, 'renderInput' | 'renderOption' | 'options'> {
    textFieldProps?: Partial<TextFieldProps>;
    form?: boolean;
    name?: string;
    required?: boolean;
    setLoadingOptions?: (loading: boolean) => void;
    renderOption?: (
        props: React.HTMLAttributes<HTMLLIElement>,
        option: WarehouseV2Option,
        state: AutocompleteRenderOptionState,
        defaultRender: (props: React.HTMLAttributes<HTMLLIElement>, option: WarehouseV2Option) => React.ReactNode
    ) => React.ReactNode;

    [key: string]: any;
}

interface WarehousesV2SearchResponse {
    data: Array<{code?: string; title?: string; name?: string; id?: number}>;
}

/**
 * Maps a ReadAllWarehouse row to the autocomplete option shape.
 */
export const mapWarehouseV2ToOption = (warehouse: {code?: string; title?: string; name?: string; id?: number} | null | undefined): WarehouseV2Option | null => {
    if (!warehouse?.code) {
        return null;
    }
    const title = warehouse.title ?? warehouse.name ?? "";
    return {
        code: warehouse.code,
        title,
        name: title,
        ...(warehouse.id != null ? {id: warehouse.id} : {}),
    };
};

/**
 * Warehouse v2 Autocomplete
 *
 * Loads partner Spire warehouses from CleanAF/Warehouse/ReadAllWarehouse.
 */
const WarehouseV2Autocomplete = <M extends boolean = false, >({
                                                                  textFieldProps = {},
                                                                  form = false,
                                                                  name = undefined,
                                                                  required = false,
                                                                  setLoadingOptions: setLoadingOptionsProps,
                                                                  renderOption: customRenderOption,
                                                                  ...props
                                                              }: WarehouseV2AutocompleteProps<M>) => {
    const isMounted = useIsMounted();
    const [loadingOptions, setLoadingOptions] = useState(false);

    /**
     * Syncs the internal loading state with the parent component
     */
    useEffect(() => {
        setLoadingOptionsProps?.(loadingOptions);
    }, [loadingOptions, setLoadingOptionsProps]);

    /**
     * Fetches all v2 warehouses and maps them to autocomplete options.
     */
    const getOptions = useCallback(async (cancelToken: CancelToken) => {
        setLoadingOptions(true);
        try {
            // @ts-expect-error Api method exists but is not yet typed
            const response = (await Api.readAllWarehouses(cancelToken)) as WarehousesV2SearchResponse;
            if (!isMounted())
                return [];
            return (response?.data ?? [])
                .map(mapWarehouseV2ToOption)
                .filter((option): option is WarehouseV2Option => option != null);
        } finally {
            if (isMounted()) {
                setLoadingOptions(false);
            }
        }
    }, [isMounted]);

    /**
     * Matches warehouses by code, and by id when both sides have one.
     */
    const isOptionEqualToValue = useCallback((option: WarehouseV2Option, value: WarehouseV2Option | null) =>
        (option.code != null && option.code === value?.code)
        || (option.id != null && option.id === value?.id), []);

    /**
     * Label is `title (code)`, falling back to code-only values used by dashboard chart clicks.
     */
    const getOptionLabel = useCallback((option: WarehouseV2Option) => {
        const title = option?.title ?? option?.name ?? "";
        if (title && option?.code) {
            return `${title} (${option.code})`;
        }
        return title || option?.code || "";
    }, []);

    /**
     * Prefers code as the stable key; falls back to id.
     */
    const getOptionKey = useCallback((option: WarehouseV2Option) =>
        option?.code
            ? `code_${option.code}`
            : (option?.id != null ? `id_${option.id}` : ""), []);

    const defaultOptions = useMemo(() => [], []);

    return (
        <AutocompleteBase<WarehouseV2Option, M>
            canSearchEmptyKeywords
            keywordsBased={false}
            getOptions={getOptions}
            timeout={1000}
            textFieldProps={textFieldProps}
            form={form}
            name={name}
            required={required}
            isOptionEqualToValue={isOptionEqualToValue}
            getOptionLabel={getOptionLabel}
            getOptionKey={getOptionKey}
            options={defaultOptions}
            clearOnBlur={false}
            {...props}
        />
    );
};

export default WarehouseV2Autocomplete;
